"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PoundSterling, Calendar, FileText, CircleCheck, Calculator, Users } from "lucide-react";
export default function PayrollServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: featuresRef,
    isVisible: featuresVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const services = [{
    icon: PoundSterling,
    title: "Payroll Processing",
    description: "Accurate and timely salary payments for your employees, ensuring compliance with all regulations and deadlines."
  }, {
    icon: FileText,
    title: "Tax\xA0",
    description: "Expert management of PAYE, National Insurance, and other statutory deductions to keep you compliant."
  }, {
    icon: Calendar,
    title: "Pension Management",
    description: "Auto-enrolment compliance and pension scheme administration for your workforce."
  }, {
    icon: Calculator,
    title: "Payslip Generation",
    description: "Professional payslips delivered digitally or in print, with full transparency for employees."
  }, {
    icon: Users,
    title: "HR Integration",
    description: "Seamless integration with your HR systems for streamlined employee data management."
  }, {
    icon: CircleCheck,
    title: "Compliance Support",
    description: "Stay up-to-date with changing payroll legislation and avoid costly penalties."
  }];
  const benefits = ["Save time with automated payroll processing", "Reduce errors and ensure accuracy", "Meet all statutory deadlines", "Comprehensive reporting and analytics", "Dedicated payroll specialist support", "Secure cloud-based access"];
  return <section id="payrollservices" className="py-16 md:py-24 bg-background">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`text-center mb-12 md:mb-16 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Payroll Services
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive payroll solutions to ensure accurate and timely payments for your team. 
            From payroll processing to tax withholdings, we handle it all with precision and care.
          </p>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 transition-all duration-700 delay-200 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={i} data-index={i} className="hover-lift border border-border">
                <CardHeader>
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary">
                    <Icon className="size-6" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
              </Card>;
        })}
        </div>

        {}
        <div ref={featuresRef} className={`bg-surface border border-border rounded-xl p-8 md:p-12 transition-all duration-700 delay-300 ${featuresVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-6 text-center">
            Why Choose Our Payroll Services?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-foreground">{benefit}</p>
              </div>)}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/team-and-contact#contact-us">Get Started</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              
            </Button>
          </div>
        </div>
      </div>
    </section>;
}